import { useEffect, useState, useRef } from 'react';
import { membersAPI, notificationsAPI } from '../../services/api';

const ITEMS_PER_PAGE = 25;
const SYNC_LIMIT = 1000; // above this, queue as a background job instead of sending inline
const DEFAULT_MESSAGE = 'Dear {name},\n\nHabari, hii ni taarifa kutoka AWTF. Ingia hapa: https://awtf.or.tz\n\nAsante.';
const EMAIL_PER_HOUR = 1000; // must match the pacing in NotificationJobRun.php

// 'sms_link' sends a short SMS pointing at a link page — no separate email;
// keep these in sync with NotificationsController::usesSms()/usesEmail().
function channelUsesSms(channel) { return channel === 'sms' || channel === 'both' || channel === 'sms_link'; }
function channelUsesEmail(channel) { return channel === 'email' || channel === 'both'; }

function formatEta(count) {
  const hours = count / EMAIL_PER_HOUR;
  if (hours < 1) return `${Math.ceil(hours * 60)} min`;
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function jobIsDone(status) { return status === 'completed' || status === 'cancelled'; }
function jobBadgeClass(status) {
  if (status === 'completed') return 'bg-emerald-100 text-emerald-700';
  if (status === 'cancelled') return 'bg-slate-200 text-slate-600';
  return 'bg-amber-100 text-amber-700';
}

async function cancelJob(jobId, onDone) {
  if (!window.confirm('Stop this notification job? Members already sent to have been sent to — this only stops the rest.')) return;
  try {
    await notificationsAPI.cancelJob(jobId);
    onDone?.();
  } catch (e) {
    alert(e.response?.data?.error || 'Failed to cancel.');
  }
}

export default function Notifications() {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [statuses, setStatuses] = useState([]);
  const [hasEmail, setHasEmail] = useState(false);
  const [hasPhone, setHasPhone] = useState(false);
  const [excludeBounced, setExcludeBounced] = useState(true);
  const [excludeSurvey, setExcludeSurvey] = useState(true);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selected, setSelected] = useState({}); // { [memberId]: { id, full_name } }
  const [allFilterCount, setAllFilterCount] = useState(null);
  const [selectingAll, setSelectingAll] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [viewingJobId, setViewingJobId] = useState(null);
  const [activeJob, setActiveJob] = useState(null);

  const loadJobs = (showSpinner = true) => {
    if (showSpinner) setJobsLoading(true);
    notificationsAPI.jobsList().then((r) => {
      const list = r.jobs || [];
      setJobs(list);
      setActiveJob(list.find((j) => j.status === 'pending' || j.status === 'running') || null);
    }).catch(() => {}).finally(() => setJobsLoading(false));
  };

  // Surfaces a still-running job immediately at the top of the page — so
  // opening this page from any device shows "how far has it reached" for an
  // overnight send, without having to scroll to Recent Notifications and click in.
  useEffect(() => {
    loadJobs();
    const t = setInterval(() => loadJobs(false), 5000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { setQuery(search); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    // Deceased members are never valid notification recipients, so they're not
    // offered as a filter here either.
    membersAPI.statuses().then((r) => setStatuses((r.statuses || []).filter((s) => s !== 'DECEASED'))).catch(() => {});
  }, []);

  const extraFilters = { has_email: hasEmail ? 1 : '', has_phone: hasPhone ? 1 : '', exclude_bounced: excludeBounced ? 1 : '', exclude_survey_done: excludeSurvey ? 1 : '' };

  useEffect(() => {
    let active = true;
    setLoading(true); setError('');
    membersAPI.list(page, ITEMS_PER_PAGE, query, status, '', 'DECEASED', extraFilters)
      .then((result) => { if (active) setData(result); })
      .catch((err) => { if (active) setError(err.response?.data?.error || 'Failed to load members.'); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [page, query, status, hasEmail, hasPhone, excludeBounced, excludeSurvey]);

  // Filter changed — the "select all matching" count is stale, clear it.
  useEffect(() => { setAllFilterCount(null); }, [query, status, hasEmail, hasPhone, excludeBounced, excludeSurvey]);

  // Re-validate existing selections against the reachability filters (Has email,
  // Has phone, exclude bounced) whenever one of those is toggled — otherwise a
  // member selected before the filter was turned on stays selected (e.g. a
  // phone-less member picked earlier, left selected after "Has phone" is later
  // checked), and gets offered as a recipient despite not matching the filter.
  useEffect(() => {
    if (Object.keys(selected).length === 0) return;
    if (!hasEmail && !hasPhone && !excludeBounced) return;
    let active = true;
    membersAPI.allIds('', '', '', 'DECEASED', {
      has_email: hasEmail ? 1 : '', has_phone: hasPhone ? 1 : '', exclude_bounced: excludeBounced ? 1 : '',
    }).then((resp) => {
      if (!active) return;
      const matching = new Set((resp.ids || []).map(Number));
      setSelected((prev) => {
        const next = {};
        for (const id in prev) { if (matching.has(Number(id))) next[id] = prev[id]; }
        return next;
      });
    }).catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasEmail, hasPhone, excludeBounced]);

  const members = data?.members || [];
  const pageAllSelected = members.length > 0 && members.every((m) => selected[m.id]);
  const selectedList = Object.values(selected);

  function toggleOne(member) {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[member.id]) delete next[member.id]; else next[member.id] = { id: member.id, full_name: member.full_name };
      return next;
    });
  }
  function togglePage() {
    setSelected((prev) => {
      const next = { ...prev };
      if (pageAllSelected) { members.forEach((m) => delete next[m.id]); }
      else { members.forEach((m) => { next[m.id] = { id: m.id, full_name: m.full_name }; }); }
      return next;
    });
  }
  function clearSelection() { setSelected({}); }

  async function selectAllMatching() {
    setSelectingAll(true);
    try {
      const resp = await membersAPI.allIds(query, status, '', 'DECEASED', extraFilters);
      setSelected((prev) => {
        const next = { ...prev };
        (resp.ids || []).forEach((id) => { if (!next[id]) next[id] = { id, full_name: null }; });
        return next;
      });
      setAllFilterCount(resp.total);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to select all matching members.');
    } finally {
      setSelectingAll(false);
    }
  }

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-slate-800">Notifications</h2>
        <p className="mt-1 text-sm text-slate-600">Select members below, then write a message to send by SMS and/or email.</p>
      </div>

      {activeJob && (
        <div className="w-full rounded-xl border border-amber-200 bg-amber-50 p-3.5">
          <button type="button" onClick={() => setViewingJobId(activeJob.id)} className="w-full text-left">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-amber-800">📤 Sending {activeJob.subject || activeJob.channel.toUpperCase()}…</span>
              <span className="text-amber-700">{activeJob.processed.toLocaleString()} / {activeJob.total.toLocaleString()}</span>
            </div>
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-amber-200">
              <div className="h-full bg-amber-500 transition-all" style={{ width: `${activeJob.total ? (activeJob.processed / activeJob.total) * 100 : 0}%` }} />
            </div>
            <p className="mt-1.5 text-xs text-amber-700">✓ {activeJob.sent.toLocaleString()} sent · ✕ {activeJob.failed.toLocaleString()} failed · tap to view details</p>
          </button>
          <div className="mt-2 flex justify-end">
            <button type="button" onClick={() => cancelJob(activeJob.id, loadJobs)}
              className="rounded-lg border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <input aria-label="Search members" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, roll number or email…"
          className="min-w-0 flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm" />
        <select aria-label="Status filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm">
          <option value="">All statuses</option>
          {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600">
          <input type="checkbox" checked={hasEmail} onChange={(e) => { setHasEmail(e.target.checked); setPage(1); }} /> Has email
        </label>
        <label className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600">
          <input type="checkbox" checked={hasPhone} onChange={(e) => { setHasPhone(e.target.checked); setPage(1); }} /> Has phone
        </label>
        <label className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600" title="Hide members whose email previously bounced permanently">
          <input type="checkbox" checked={excludeBounced} onChange={(e) => { setExcludeBounced(e.target.checked); setPage(1); }} /> Exclude bounced
        </label>
        <label className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600" title="Hide members who already filled the health insurance survey">
          <input type="checkbox" checked={excludeSurvey} onChange={(e) => { setExcludeSurvey(e.target.checked); setPage(1); }} /> Exclude survey done
        </label>
        <button type="button" disabled={selectedList.length === 0} onClick={() => setComposeOpen(true)}
          className="rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-orange-500 disabled:opacity-40">
          📣 Notify {selectedList.length > 0 ? `(${selectedList.length})` : ''}
        </button>
      </div>

      {selectedList.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-orange-200 bg-orange-50 px-4 py-2.5 text-xs text-orange-800">
          <span className="font-semibold">{selectedList.length.toLocaleString()} selected</span>
          {allFilterCount === null && data?.total > members.length && (
            <button type="button" onClick={selectAllMatching} disabled={selectingAll}
              className="rounded-lg border border-orange-300 bg-white px-2.5 py-1 font-semibold text-orange-700 hover:bg-orange-100 disabled:opacity-50">
              {selectingAll ? 'Selecting…' : `Select all ${data.total.toLocaleString()} matching this filter`}
            </button>
          )}
          <button type="button" onClick={clearSelection} className="ml-auto text-orange-600 hover:text-orange-800 underline">Clear selection</button>
        </div>
      )}

      {loading ? <p role="status" className="py-8 text-center text-slate-500">Loading members…</p> : error ? <p role="alert" className="rounded-xl bg-red-50 p-4 text-red-700">{error}</p> : <>
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-600">{data?.total?.toLocaleString() || 0} members match this filter</p>
          <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600 sm:hidden">
            <input type="checkbox" checked={pageAllSelected} onChange={togglePage} /> Select all on page
          </label>
        </div>

        {/* Desktop / wide screens: table */}
        <div className="hidden overflow-x-auto rounded-xl border border-slate-200 bg-white sm:block">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-slate-600"><tr>
              <th className="whitespace-nowrap px-4 py-3"><input type="checkbox" checked={pageAllSelected} onChange={togglePage} aria-label="Select all on this page" /></th>
              {['Member', 'Roll No.', 'Status', 'Phone', 'Email'].map((label) => <th key={label} className="whitespace-nowrap px-4 py-3 font-semibold">{label}</th>)}
            </tr></thead>
            <tbody>{members.map((member) => (
              <tr key={member.id} className={`border-t border-slate-100 ${selected[member.id] ? 'bg-orange-50/50' : ''}`}>
                <td className="px-4 py-3"><input type="checkbox" checked={!!selected[member.id]} onChange={() => toggleOne(member)} aria-label={`Select ${member.full_name}`} /></td>
                <td className="min-w-48 px-4 py-3 font-medium text-slate-800">{member.full_name}</td>
                <td className="px-4 py-3">{member.roll_no || '—'}</td>
                <td className="whitespace-nowrap px-4 py-3">{member.status || '—'}</td>
                <td className="whitespace-nowrap px-4 py-3">{member.phone || '—'}</td>
                <td className="px-4 py-3">
                  {member.email || '—'}
                  {member.email_bounced_at && <span title={member.email_bounce_reason || 'Bounced'} className="ml-1.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">bounced</span>}
                </td>
              </tr>
            ))}</tbody>
          </table>
          {!members.length && <p className="p-8 text-center text-slate-500">No members match this filter.</p>}
        </div>

        {/* Mobile: cards instead of a cramped table */}
        <div className="divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white sm:hidden">
          {members.map((member) => (
            <label key={member.id} className={`flex items-start gap-3 p-3 ${selected[member.id] ? 'bg-orange-50/50' : ''}`}>
              <input type="checkbox" checked={!!selected[member.id]} onChange={() => toggleOne(member)}
                aria-label={`Select ${member.full_name}`} className="mt-1 shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-800">{member.full_name}</p>
                <p className="mt-0.5 text-xs text-slate-500">
                  {member.roll_no && <>Roll {member.roll_no} · </>}{member.status || '—'}
                </p>
                <p className="mt-0.5 truncate text-xs text-slate-500">
                  {member.phone || '—'}{member.email && <> · {member.email}</>}
                  {member.email_bounced_at && <span className="ml-1.5 rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">bounced</span>}
                </p>
              </div>
            </label>
          ))}
          {!members.length && <p className="p-8 text-center text-slate-500">No members match this filter.</p>}
        </div>

        <div className="flex items-center justify-between gap-2 text-sm">
          <span>Page {data?.page || 1} of {data?.total_pages || 1}</span>
          <div className="flex gap-2">
            <button type="button" disabled={!data?.has_previous} onClick={() => setPage((p) => p - 1)} className="rounded-lg border px-3 py-2 disabled:opacity-40">Previous</button>
            <button type="button" disabled={!data?.has_next} onClick={() => setPage((p) => p + 1)} className="rounded-lg border px-3 py-2 disabled:opacity-40">Next</button>
          </div>
        </div>
      </>}

      <div className="pt-2">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-slate-800">Recent Notifications</h3>
          <button type="button" onClick={loadJobs} disabled={jobsLoading} className="text-xs font-semibold text-orange-600 hover:text-orange-800 disabled:opacity-50">Refresh</button>
        </div>
        {jobsLoading ? (
          <p className="py-4 text-center text-sm text-slate-400">Loading…</p>
        ) : jobs.length === 0 ? (
          <p className="py-4 text-sm text-slate-400">No notifications sent yet.</p>
        ) : (
          <>
            {/* Desktop / wide screens: table */}
            <div className="mt-2 hidden overflow-x-auto rounded-xl border border-slate-200 bg-white sm:block">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-slate-600"><tr>
                  {['Subject / Channel', 'Progress', 'Sent', 'Failed', 'Status', 'Started'].map((label) => <th key={label} className="whitespace-nowrap px-4 py-2.5 font-semibold">{label}</th>)}
                </tr></thead>
                <tbody>{jobs.map((j) => (
                  <tr key={j.id} onClick={() => setViewingJobId(j.id)} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-slate-800">{j.subject || j.channel.toUpperCase()}</p>
                      <p className="text-xs uppercase text-slate-400">{j.channel}</p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5">{j.processed.toLocaleString()} / {j.total.toLocaleString()}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-emerald-600">{j.sent.toLocaleString()}</td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-red-600">{j.failed.toLocaleString()}</td>
                    <td className="whitespace-nowrap px-4 py-2.5">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${jobBadgeClass(j.status)}`}>{j.status}</span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">{j.created_at?.slice(0, 16)}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>

            {/* Mobile: cards instead of a cramped table */}
            <div className="mt-2 divide-y divide-slate-100 rounded-xl border border-slate-200 bg-white sm:hidden">
              {jobs.map((j) => (
                <button key={j.id} type="button" onClick={() => setViewingJobId(j.id)} className="block w-full p-3 text-left hover:bg-slate-50">
                  <div className="flex items-center justify-between gap-2">
                    <p className="min-w-0 truncate font-medium text-slate-800">{j.subject || j.channel.toUpperCase()}</p>
                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${jobBadgeClass(j.status)}`}>{j.status}</span>
                  </div>
                  <p className="mt-0.5 text-xs uppercase text-slate-400">{j.channel} · {j.created_at?.slice(0, 16)}</p>
                  <p className="mt-1 text-xs text-slate-600">{j.processed.toLocaleString()} / {j.total.toLocaleString()} · <span className="text-emerald-600">✓ {j.sent.toLocaleString()}</span> · <span className="text-red-600">✕ {j.failed.toLocaleString()}</span></p>
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {composeOpen && (
        <ComposeModal
          recipients={selectedList}
          excludeSurvey={excludeSurvey}
          onClose={() => setComposeOpen(false)}
          onSent={() => { clearSelection(); setComposeOpen(false); loadJobs(); }}
        />
      )}

      {viewingJobId && (
        <JobHistoryModal jobId={viewingJobId}
          onClose={() => { setViewingJobId(null); loadJobs(); }}
          onResent={(newJobId) => { setViewingJobId(newJobId); loadJobs(); }} />
      )}
    </section>
  );
}

function ComposeModal({ recipients, excludeSurvey, onClose, onSent }) {
  const [channel, setChannel] = useState('sms');
  const [subject, setSubject] = useState('AWTF Notification');
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // sync result
  const [job, setJob] = useState(null); // async job status
  const [remaining, setRemaining] = useState(0); // not-yet-reached count from the batch_size cap (email/both only)
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  async function handleSend() {
    if (!message.trim()) { setError('Message text is required.'); return; }
    setSending(true); setError(''); setJob(null); setResult(null); setRemaining(0);
    const ids = recipients.map((m) => m.id);
    // Email needs to be paced slowly (see notifications:process) to avoid Gmail/
    // Yahoo flagging a burst as spam — that pacing only happens in the queued
    // background job, so any email send always goes through it, not just large ones.
    const needsQueue = channelUsesEmail(channel) || ids.length > SYNC_LIMIT;
    try {
      if (needsQueue) {
        const resp = await notificationsAPI.queue(ids, channel, message.trim(), subject.trim(), { exclude_survey_done: excludeSurvey });
        setJob({ id: resp.job_id, total: resp.total, processed: 0, sent: 0, failed: 0, status: 'pending' });
        setRemaining(resp.remaining_after_batch || 0);
        pollRef.current = setInterval(async () => {
          try {
            const r = await notificationsAPI.jobStatus(resp.job_id);
            setJob(r.job);
            if (jobIsDone(r.job.status)) clearInterval(pollRef.current);
          } catch { /* keep polling on transient errors */ }
        }, 3000);
      } else {
        const resp = await notificationsAPI.send(ids, channel, message.trim(), subject.trim(), { exclude_survey_done: excludeSurvey });
        setResult(resp);
        setRemaining(resp.remaining_after_batch || 0);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to send notifications.');
    } finally {
      setSending(false);
    }
  }

  const noPhoneCount = channelUsesSms(channel) ? recipients.filter((m) => m.full_name && !m.phone).length : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 px-4 py-6">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-800">Notify {recipients.length.toLocaleString()} member{recipients.length === 1 ? '' : 's'}</h3>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">✕</button>
        </div>

        {job ? (
          <div className="mt-4 space-y-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-slate-700">{job.status === 'completed' ? 'Done' : job.status === 'cancelled' ? 'Cancelled' : 'Sending…'}</span>
                <span className="text-slate-500">{job.processed.toLocaleString()} / {job.total.toLocaleString()}</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                <div className="h-full bg-orange-500 transition-all" style={{ width: `${job.total ? (job.processed / job.total) * 100 : 0}%` }} />
              </div>
              <p className="mt-2 text-xs text-slate-500">✓ {job.sent.toLocaleString()} sent · ✕ {job.failed.toLocaleString()} failed</p>
              {job.status === 'running' && <p className="mt-1 text-[11px] text-slate-400">This runs in the background — you can close this and check back later (see it under Recent Notifications).</p>}
            </div>
            {jobIsDone(job.status) && remaining > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-sm font-semibold text-amber-800">{remaining.toLocaleString()} member{remaining === 1 ? '' : 's'} not reached yet</p>
                <p className="mt-1 text-xs text-amber-700">This send only covers a batch (see "Notification batch size" in System Settings). Click below with the same message to continue — already-reached members are skipped automatically.</p>
                <button type="button" onClick={handleSend} disabled={sending}
                  className="mt-2 rounded-xl bg-amber-600 px-4 py-2 text-xs font-semibold text-white hover:bg-amber-500 disabled:opacity-60">
                  Send to remaining {remaining.toLocaleString()}
                </button>
              </div>
            )}
            <JobResultsTable jobId={job.id} />
            <div className="flex justify-end gap-2">
              {!jobIsDone(job.status) && (
                <button type="button"
                  onClick={() => cancelJob(job.id, () => notificationsAPI.jobStatus(job.id).then((r) => setJob(r.job)))}
                  className="rounded-xl border border-red-300 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">
                  Cancel
                </button>
              )}
              <button type="button" onClick={onSent} className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900">
                {jobIsDone(job.status) ? 'Done' : 'Close (keeps sending)'}
              </button>
            </div>
          </div>
        ) : result ? (
          <div className="mt-4 space-y-3">
            <p className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{result.message}</p>
            {remaining > 0 && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-sm font-semibold text-amber-800">{remaining.toLocaleString()} member{remaining === 1 ? '' : 's'} not reached yet</p>
                <p className="mt-1 text-xs text-amber-700">This send only covers a batch (see "Notification batch size" in System Settings). Click below with the same message to continue — already-reached members are skipped automatically.</p>
                <button type="button" onClick={handleSend} disabled={sending}
                  className="mt-2 rounded-xl bg-amber-600 px-4 py-2 text-xs font-semibold text-white hover:bg-amber-500 disabled:opacity-60">
                  Send to remaining {remaining.toLocaleString()}
                </button>
              </div>
            )}
            <div className="max-h-56 overflow-y-auto rounded-xl border border-slate-200">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-500"><tr><th className="px-3 py-2">Member</th>{channelUsesSms(channel) && <th className="px-3 py-2">SMS</th>}{channelUsesEmail(channel) && <th className="px-3 py-2">Email</th>}</tr></thead>
                <tbody>{result.results.map((r) => (
                  <tr key={r.member_id} className="border-t border-slate-100">
                    <td className="px-3 py-2 text-slate-700">{r.full_name}</td>
                    {channelUsesSms(channel) && <td className={`px-3 py-2 ${r.sms === 'sent' ? 'text-emerald-600' : 'text-red-600'}`}>{r.sms || '—'}</td>}
                    {channelUsesEmail(channel) && <td className={`px-3 py-2 ${r.email === 'sent' ? 'text-emerald-600' : 'text-red-600'}`}>{r.email || '—'}</td>}
                  </tr>
                ))}</tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <button type="button" onClick={onSent} className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900">Done</button>
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {error && <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Channel</label>
              <div className="flex gap-2">
                {[['sms', 'SMS'], ['email', 'Email'], ['both', 'SMS + Email'], ['sms_link', 'SMS + Link']].map(([key, label]) => (
                  <button key={key} type="button" onClick={() => setChannel(key)}
                    className={`rounded-xl border px-3 py-2 text-xs font-semibold ${channel === key ? 'border-orange-500 bg-orange-50 text-orange-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                    {label}
                  </button>
                ))}
              </div>
              {noPhoneCount > 0 && <p className="mt-1.5 text-[11px] text-amber-600">{noPhoneCount} of the members shown on this page have no phone on file.</p>}
            </div>

            {channel !== 'sms' && (
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">
                  {channel === 'sms_link' ? 'Message heading (shown on the link page)' : 'Email subject'}
                </label>
                <input value={subject} onChange={(e) => setSubject(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Message</label>
              <p className="mb-1 text-[11px] text-slate-500">Tumia <code className="rounded bg-slate-100 px-1">{"{name}"}</code> kuweka jina la kila mwanachama kiotomatiki, mfano: Dear {"{name}"},</p>
              <textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" />
              {channel === 'sms_link' && (
                <p className="mt-1 text-[11px] text-slate-500">SMS fupi itatumwa ikiwa na kiungo cha kusoma ujumbe huu mzima — hakuna haja ya kuuandika mfupi.</p>
              )}
            </div>

            {(channelUsesEmail(channel) || recipients.length > SYNC_LIMIT) && (
              <p className="text-[11px] text-slate-500">
                {channelUsesEmail(channel)
                  ? <>Email is sent gradually in the background (~1,000/hour) to avoid Gmail/Yahoo flagging it as spam — for {recipients.length.toLocaleString()} recipients that's roughly <strong>{formatEta(recipients.length)}</strong>. You'll see live progress here; it keeps sending even if you close this.</>
                  : <>This is a large group ({recipients.length.toLocaleString()}) — it will be sent gradually in the background. You'll see live progress here.</>}
              </p>
            )}

            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} disabled={sending}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60">
                Cancel
              </button>
              <button type="button" onClick={handleSend} disabled={sending}
                className="rounded-xl bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white px-4 py-2 text-sm font-semibold">
                {sending ? 'Sending…' : `Send to ${recipients.length.toLocaleString()}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function JobResultsTable({ jobId }) {
  const [filter, setFilter] = useState(''); // '' | 'sent' | 'failed'
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    notificationsAPI.jobResults(jobId, page, filter)
      .then((r) => { if (active) setData(r); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [jobId, page, filter]);

  return (
    <div className="rounded-xl border border-slate-200">
      <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
        <div className="flex gap-1.5">
          {[['', 'All'], ['sent', 'Sent'], ['failed', 'Failed']].map(([key, label]) => (
            <button key={key} type="button" onClick={() => { setFilter(key); setPage(1); }}
              className={`rounded-lg px-2.5 py-1 text-xs font-semibold ${filter === key ? 'bg-orange-100 text-orange-700' : 'text-slate-500 hover:bg-slate-100'}`}>
              {label}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-400">{data?.total?.toLocaleString() ?? '—'} recipient{data?.total === 1 ? '' : 's'}</span>
      </div>
      {loading ? (
        <p className="p-4 text-center text-xs text-slate-400">Loading…</p>
      ) : (
        <>
          <div className="max-h-64 overflow-y-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-500 sticky top-0"><tr>
                <th className="px-3 py-2">Member</th>
                {channelUsesSms(data?.channel) && <th className="px-3 py-2">SMS</th>}
                {channelUsesEmail(data?.channel) && <th className="px-3 py-2">Email</th>}
              </tr></thead>
              <tbody>{(data?.results || []).map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 text-slate-700">{r.full_name || `Member #${r.member_id}`}</td>
                  {channelUsesSms(data?.channel) && <td className={`px-3 py-2 ${r.sms_status === 'sent' ? 'text-emerald-600' : 'text-red-600'}`}>{r.sms_status || '—'}</td>}
                  {channelUsesEmail(data?.channel) && <td className={`px-3 py-2 ${r.email_status === 'sent' ? 'text-emerald-600' : 'text-red-600'}`}>{r.email_status || '—'}</td>}
                </tr>
              ))}</tbody>
            </table>
            {!data?.results?.length && <p className="p-4 text-center text-xs text-slate-400">No recipients processed yet.</p>}
          </div>
          {(data?.total_pages ?? 1) > 1 && (
            <div className="flex items-center justify-between border-t border-slate-100 px-3 py-2 text-xs">
              <span>Page {data.page} of {data.total_pages}</span>
              <div className="flex gap-1.5">
                <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="rounded-lg border px-2 py-1 disabled:opacity-40">Prev</button>
                <button type="button" disabled={page >= data.total_pages} onClick={() => setPage((p) => p + 1)} className="rounded-lg border px-2 py-1 disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function JobHistoryModal({ jobId, onClose, onResent }) {
  const [job, setJob] = useState(null);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const pollRef = useRef(null);
  const fetchStatusRef = useRef(null);

  async function handleResendRemaining() {
    setResending(true); setResendMsg('');
    try {
      const resp = await notificationsAPI.resendRemaining(jobId);
      if (resp.total > 0) {
        onResent(resp.job_id);
      } else {
        setResendMsg(resp.message || 'Everyone has already been reached.');
      }
    } catch (e) {
      setResendMsg(e.response?.data?.error || 'Failed to resend.');
    } finally {
      setResending(false);
    }
  }

  useEffect(() => {
    const fetchStatus = () => notificationsAPI.jobStatus(jobId).then((r) => {
      setJob(r.job);
      if (jobIsDone(r.job.status) && pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }).catch(() => {});
    fetchStatusRef.current = fetchStatus;
    fetchStatus();
    pollRef.current = setInterval(fetchStatus, 4000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [jobId]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 px-4 py-6">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold text-slate-800">{job?.subject || 'Notification'} <span className="font-normal text-slate-400">#{jobId}</span></h3>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">✕</button>
        </div>

        {!job ? (
          <p className="mt-4 py-4 text-center text-sm text-slate-400">Loading…</p>
        ) : (
          <div className="mt-4 space-y-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="font-semibold text-slate-700">{job.status === 'completed' ? 'Done' : job.status === 'cancelled' ? 'Cancelled' : 'Sending…'}</span>
                <span className="text-slate-500">{job.processed.toLocaleString()} / {job.total.toLocaleString()}</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-200">
                <div className="h-full bg-orange-500 transition-all" style={{ width: `${job.total ? (job.processed / job.total) * 100 : 0}%` }} />
              </div>
              <p className="mt-2 text-xs text-slate-500">✓ {job.sent.toLocaleString()} sent · ✕ {job.failed.toLocaleString()} failed</p>
            </div>
            <JobResultsTable jobId={jobId} />
            {jobIsDone(job.status) && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <p className="text-xs text-amber-700">If this send only reached a batch (email/both have a batch size limit — see System Settings), you can finish it here — the same message, only to those not yet reached.</p>
                <button type="button" onClick={handleResendRemaining} disabled={resending}
                  className="mt-2 rounded-xl bg-amber-600 px-4 py-2 text-xs font-semibold text-white hover:bg-amber-500 disabled:opacity-60">
                  {resending ? 'Checking…' : 'Send to remaining'}
                </button>
                {resendMsg && <p className="mt-1.5 text-xs text-slate-600">{resendMsg}</p>}
              </div>
            )}
            <div className="flex justify-end gap-2">
              {!jobIsDone(job.status) && (
                <button type="button" onClick={() => cancelJob(jobId, () => fetchStatusRef.current?.())}
                  className="rounded-xl border border-red-300 px-4 py-2 text-sm font-semibold text-red-600 hover:bg-red-50">
                  Cancel
                </button>
              )}
              <button type="button" onClick={onClose} className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
